import { Component, OnInit, ViewChild } from '@angular/core';
import { Location } from '@angular/common';
import { ConfigStateService } from '@abp/ng.core';
import { ActivatedRoute } from '@angular/router';
import { OrderService, OrderDto, OrderItemDto } from '@proxy/orders';
import { WineService, WineDto } from '@proxy/wines';
import { APP_ICONS } from '../shared/icons/app-icons';
import { StorageService } from '../shared/services/storage.service';
import { OrderStatus } from '@proxy/enums/order-status.enum';
import { SommelierService } from '@proxy/sommeliers';
import { DeliverOrderFormComponent } from '../deliveries/deliver-order-form/deliver-order-form.component';
import { NotificationService } from '../shared/services/notification.service';

interface ExtendedOrderItemDto extends OrderItemDto {
  wine?: WineDto;
}

interface ExtendedOrderDto extends OrderDto {
  orderItems: ExtendedOrderItemDto[];
}

@Component({
  selector: 'app-order-details',
  standalone: false,
  templateUrl: './order-details.component.html'
})
export class OrderDetailsComponent implements OnInit {
  order: ExtendedOrderDto | null = null;
  isLoading = true;
  error: string | null = null;
  iconBack = APP_ICONS.arrow;
  showDeliverButton = false;
  isClient = false;
  isSommelier = false;
  @ViewChild(DeliverOrderFormComponent)
  deliverForm!: DeliverOrderFormComponent;


  constructor(
    private route: ActivatedRoute,
    private orderService: OrderService,
    private wineService: WineService,
    private storageService: StorageService,
    private configState: ConfigStateService,
    private location: Location,
    private sommelierService: SommelierService,
    private notificationService: NotificationService,
  ) { }

  formatCustomerAddress(order: ExtendedOrderDto): string {
    return [order.customerAddress, order.customerProvince].filter(p => !!p).join(', ');
  }

  showDeliverModal = false;

  openDeliverModal(): void {
    this.showDeliverModal = true;
  }

  onDeliverConfirm(code: string): void {
    if (!this.order) return;

    this.sommelierService.deliverOrder(this.order.id, code, {
      skipHandleError: true
    }).subscribe({
      next: () => {
        this.showDeliverModal = false;
        this.notificationService.success(
          '::Success',
          '::OrderDeliveredSuccessfully'
        );
        this.loadOrderDetails(this.order.id);
      },
      error: (err) => {
        const errorCode = err?.error?.error?.code;
        if (errorCode === 'Order:InvalidDeliveryCode') {
          this.deliverForm?.setInvalidCode();
        }
      }

    });
  }


  ngOnInit(): void {
    const orderIdParam = this.route.snapshot.paramMap.get('id');

    if (orderIdParam) {
      const orderId = +orderIdParam; // Convertir a número
      if (!isNaN(orderId)) {
        this.loadOrderDetails(orderId);
      } else {
        this.error = 'ID de orden inválido';
        this.isLoading = false;
      }
    } else {
      this.error = 'No se proporcionó ID de orden';
      this.isLoading = false;
    }

    this.checkDeliverButtonVisibility();
  }

  private checkDeliverButtonVisibility(): void {
    const fromDeliveries = this.route.snapshot.queryParams['fromDeliveries'] === 'true';
    const currentUser = this.configState.getOne("currentUser");
    const roles = currentUser?.roles || [];
    const isPickup = roles.some(r => r.toLowerCase() === 'pickup');
    this.isClient = roles.some(r => r.toLowerCase() === 'client');
    this.isSommelier = roles.some(r => r.toLowerCase() === 'sommelier');

    this.showDeliverButton =
      fromDeliveries && isPickup && this.order?.orderStatus === OrderStatus.Paid;
  }

  loadOrderDetails(orderId: number): void {
    // Usar el método get() que está disponible en el servicio
    // Este método llama a GetAsync en el backend que ya tiene la lógica de autorización
    this.orderService.get(orderId).subscribe({
      next: (result) => {
        this.order = result as ExtendedOrderDto;

        // Cargar la información detallada de los vinos para cada item
        if (this.order.orderItems && this.order.orderItems.length > 0) {
          this.loadWineDetails();
        } else {
          this.isLoading = false;
        }

        this.checkDeliverButtonVisibility();
      },
      error: (err) => {
        console.error('Error al cargar los detalles de la orden:', err);
        // Verificar si es un error de autorización
        if (err.status === 403 || err.status === 404) {
          this.error = 'No tienes permiso para ver esta orden o la orden no existe';
        } else {
          this.error = 'No se pudo cargar la información de la orden';
        }
        this.isLoading = false;
      }
    });
  }

  loadWineDetails(): void {
    if (!this.order) return;

    const wineIds = this.order.orderItems
      .map(item => item.wineId)
      .filter(id => id !== undefined) as number[];

    if (wineIds.length === 0) {
      this.isLoading = false;
      return;
    }

    // Para cada vino, cargar su información detallada (incluyendo eliminados)
    const wineRequests = wineIds.map(wineId =>
      this.wineService.getIncludingDeleted(wineId).toPromise()
    );

    Promise.all(wineRequests).then(wineResults => {
      // Asignar la información del vino a cada item de orden
      this.order!.orderItems.forEach(item => {
        const wineDetail = wineResults.find(wine => wine && wine.id === item.wineId);
        if (wineDetail) {
          item.wine = wineDetail;
          this.loadWineImage(item);
        }
      });
      this.isLoading = false;
    }).catch(error => {
      console.error('Error al cargar detalles de los vinos:', error);
      this.isLoading = false;
    });
  }

  goBack(): void {
    this.location.back();
  }

  printOrder(): void {
    window.print();
  }

  getOrderStatusString(status: number | undefined): string {
    if (status === undefined) return 'Unknown';

    switch (status) {
      case 0: return 'Pending';
      case 1: return 'Paid';
      case 2: return 'Shipped';
      case 3: return 'Delivered';
      case 4: return 'Cancelled';
      default: return 'Unknown';
    }
  }

  loadWineImage(item: ExtendedOrderItemDto): void {
    const fileId = item.wine?.fileAttachmentId;

    if (fileId) {
      this.storageService.getImage(fileId, 100, 200).subscribe({
        next: (blob) => {
          item.wine.imageUrl = URL.createObjectURL(blob);
        },
        error: () => {
          item.wine.imageUrl = 'assets/images/no-image.png';
        }
      });
    } else {
      item.wine.imageUrl = 'assets/images/no-image.png';
    }
  }
}
